import React, { useRef, useState } from "react";
import {
  BUCKET_DROPPABLE_CONTAINER_ID,
  MOUSE_CONSTRAINT_DISTANCE,
} from "@miplatform/constants";
import type {
  DragEndEvent,
  DragStartEvent,
  Modifier,
  SensorDescriptor,
  SensorOptions,
} from "@dnd-kit/core";
import { MouseSensor, TouchSensor, useSensor, useSensors } from "@dnd-kit/core";
import type {
  DraggingEvent,
  GetColumnsParams,
  SchedulerColumnTuple,
  SchedulerDate,
  SchedulerEvent,
  SchedulerUpdates,
  TimeInterval,
} from "../types";
import { useDayViewViewModel } from "./useDayViewViewModel";

export const useDayViewController = ({
  onEventUpdate,
  onEventCreate,
  date,
  intervals,
  zoom,
}: UseDayViewControllerParams): UseDayViewControllerReturn => {
  const [draggingEvent, setDraggingEvent] = useState<DraggingEvent | null>(
    null
  );
  const [selectedEventId, setSelectedEventId] = useState<string>("");
  const [columns, setColumns] = useState<SchedulerColumnTuple[]>([]);

  const mouseSensor = useSensor(MouseSensor, {
    activationConstraint: {
      distance: MOUSE_CONSTRAINT_DISTANCE,
    },
  });
  const touchSensor = useSensor(TouchSensor, {
    activationConstraint: {
      distance: MOUSE_CONSTRAINT_DISTANCE,
    },
  });
  const sensors = useSensors(mouseSensor, touchSensor);

  const { getColumnsFromData, getUpdatesOnDragEnd, snapModifier } =
    useDayViewViewModel({ date, intervals, zoom });
  const columnHeaderAreaRef = useRef<HTMLDivElement>(null);
  const calendarRef = useRef<HTMLDivElement>(null);

  const prepareColumns = (config: GetColumnsParams): void => {
    setColumns(getColumnsFromData(config));
  };

  const handleDragStart = (event: DragStartEvent): void => {
    setDraggingEvent({ ...event.active.data.current, y: 0 } as DraggingEvent);
  };

  const handleDragCancel = (): void => {
    setDraggingEvent(null);
    setSelectedEventId("");
  };

  const handleDragEnd = (event: DragEndEvent): void => {
    handleDragCancel();

    if (!event.over || event.over?.id === BUCKET_DROPPABLE_CONTAINER_ID) {
      return;
    }

    const updates = getUpdatesOnDragEnd(event);
    if (!updates) {
      return;
    }

    const item = event.active.data.current as SchedulerEvent;

    const handler = item.fromBucket ? onEventCreate : onEventUpdate;
    handler?.(item, updates);
  };

  const handleScroll =
    (ref: React.RefObject<HTMLDivElement | null>) =>
    (event: React.UIEvent<HTMLDivElement>): void => {
      if (ref.current) {
        ref.current.scrollLeft = (event.target as HTMLDivElement).scrollLeft;
      }
    };

  return {
    sensors,
    columns,
    draggingEvent,
    columnHeaderAreaRef,
    calendarRef,
    selectedEventId,
    setSelectedEventId,
    prepareColumns,
    handleDragStart,
    handleDragCancel,
    handleDragEnd,
    handleScroll,
    snapModifier,
  };
};

export interface UseDayViewControllerParams {
  onEventUpdate?: (item: SchedulerEvent, updates: SchedulerUpdates) => void;
  onEventCreate?: (item: SchedulerEvent, updates: SchedulerUpdates) => void;
  date: SchedulerDate;
  intervals: TimeInterval[];
  zoom: number;
}

export interface UseDayViewControllerReturn {
  sensors: SensorDescriptor<SensorOptions>[];
  columns: SchedulerColumnTuple[];
  draggingEvent: DraggingEvent | null;
  columnHeaderAreaRef: React.RefObject<HTMLDivElement | null>;
  calendarRef: React.RefObject<HTMLDivElement | null>;
  selectedEventId: string;
  setSelectedEventId: React.Dispatch<React.SetStateAction<string>>;
  prepareColumns: (config: GetColumnsParams) => void;
  handleDragStart: (event: DragStartEvent) => void;
  handleDragCancel: () => void;
  handleDragEnd: (event: DragEndEvent) => void;
  handleScroll: (
    ref: React.RefObject<HTMLDivElement | null>
  ) => (event: React.UIEvent<HTMLDivElement>) => void;
  snapModifier: Modifier;
}
